import React from "react"

import { Loader2, Search } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { LoadingUnitType, LocationStatus } from "@/types/collections"

import SamsaraLocationFields from "./SamsaraLocationFields"
import { LocationDialogState } from "./useLocationDialog"

const LocationDialog: React.FC<{ dialog: LocationDialogState }> = ({ dialog }) => {
  const {
    open,
    setOpen,
    editing,
    form,
    setForm,
    customerSearchTerm,
    setCustomerSearchTerm,
    filteredCustomers,
    samsaraFailed,
    setSamsaraFailed,
    save,
    isSaving,
  } = dialog
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Location" : "Add Location"}</DialogTitle>
          <DialogDescription>
            {editing ? "Update location details" : "Create a new location record"}
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="siteName">Site Name *</Label>
              <Input
                id="siteName"
                value={form.site_name || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, site_name: e.target.value }))
                }
                className="bg-white"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="internalId">Internal ID</Label>
              <Input
                id="internalId"
                value={form.internal_id || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, internal_id: e.target.value }))
                }
                className="bg-white"
                placeholder="Optional"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="contactName">Contact Name</Label>
              <Input
                id="contactName"
                value={form.contact_name || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, contact_name: e.target.value }))
                }
                className="bg-white"
                placeholder="Optional"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contactPhone">Contact Phone</Label>
              <Input
                id="contactPhone"
                type="tel"
                value={form.contact_phone || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, contact_phone: e.target.value }))
                }
                className="bg-white"
                placeholder="Optional"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="customerId" className="font-semibold">
              Customers *
            </Label>
            <p className="text-xs text-palette-jungleGreen500 -mt-1 mb-1">
              Select one or more customers for this location
            </p>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-palette-jungleGreen400" />
              <Input
                placeholder="Search customers by name or account ID..."
                value={customerSearchTerm}
                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                className="pl-9 bg-white border-palette-jungleGreen200 h-9 text-sm"
              />
            </div>
            <div className="border rounded-md bg-white max-h-[180px] overflow-y-auto">
              {filteredCustomers.length === 0 ? (
                <div className="p-4 text-center text-palette-jungleGreen500 text-sm">
                  {customerSearchTerm
                    ? "No customers match your search"
                    : "No customers available"}
                </div>
              ) : (
                <div className="divide-y">
                  {filteredCustomers.map((customer) => {
                    const isSelected = form.customer_ids?.includes(customer.id) || false
                    return (
                      <div
                        key={customer.id}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-palette-jungleGreen000 transition-colors",
                          isSelected && "bg-amber-50",
                        )}
                        onClick={() => {
                          setForm((prev) => {
                            const currentIds = prev.customer_ids || []
                            if (currentIds.includes(customer.id)) {
                              const newIds = currentIds.filter((id) => id !== customer.id)
                              return {
                                ...prev,
                                customer_ids: newIds,
                                customer_id: newIds[0] || "",
                              }
                            } else {
                              const newIds = [...currentIds, customer.id]
                              return {
                                ...prev,
                                customer_ids: newIds,
                                customer_id: newIds[0] || customer.id,
                              }
                            }
                          })
                        }}
                      >
                        <Checkbox checked={isSelected} className="pointer-events-none" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">{customer.name}</span>
                          </div>
                          <span className="text-xs text-palette-jungleGreen500">
                            {customer.account_id}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            {(form.customer_ids?.length || 0) > 1 && (
              <p className="text-xs text-palette-jungleGreen500">
                <span className="font-medium">
                  {form.customer_ids?.length} customers selected.
                </span>
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="address">Address *</Label>
            <Input
              id="address"
              value={form.address || ""}
              onChange={(e) => setForm((prev) => ({ ...prev, address: e.target.value }))}
              className="bg-white"
            />
          </div>
          <SamsaraLocationFields
            variant="location"
            form={form}
            setForm={setForm}
            samsaraFailed={samsaraFailed}
            setSamsaraFailed={setSamsaraFailed}
            editing={!!editing}
          />
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="openingTime">Opening Time</Label>
              <Input
                id="openingTime"
                type="time"
                value={form.opening_time || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, opening_time: e.target.value }))
                }
                className="bg-white"
                placeholder="Optional"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="closingTime">Closing Time</Label>
              <Input
                id="closingTime"
                type="time"
                value={form.closing_time || ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, closing_time: e.target.value }))
                }
                className="bg-white"
                placeholder="Optional"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label htmlFor="loadingUnitType">Loading Unit Type</Label>
              <Select
                value={form.loading_unit_type || ""}
                onValueChange={(value: LoadingUnitType) =>
                  setForm((prev) => ({ ...prev, loading_unit_type: value }))
                }
              >
                <SelectTrigger className="bg-white">
                  <SelectValue placeholder="Select type (optional)" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="Pallets (UK)">Pallets (UK)</SelectItem>
                  <SelectItem value="Pallets (EUR)">Pallets (EUR)</SelectItem>
                  <SelectItem value="Cages">Cages</SelectItem>
                  <SelectItem value="Loose Load">Loose Load</SelectItem>
                  <SelectItem value="Magnums">Magnums</SelectItem>
                  <SelectItem value="Sort Bags">Sort Bags</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="parcelsPerUnit">Parcels per Loading Unit</Label>
              <Input
                id="parcelsPerUnit"
                type="number"
                value={form.parcels_per_loading_unit || ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    parcels_per_loading_unit: parseInt(e.target.value),
                  }))
                }
                className="bg-white"
                placeholder="Optional"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={form.status as string}
              onValueChange={(value: LocationStatus) =>
                setForm((prev) => ({ ...prev, status: value }))
              }
            >
              <SelectTrigger className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="collectionProtocol">Collection Protocol</Label>
            <Textarea
              id="collectionProtocol"
              value={form.collection_protocol || ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, collection_protocol: e.target.value }))
              }
              className="bg-white"
              rows={2}
              placeholder="Instructions for drivers..."
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => {
              setOpen(false)
              setCustomerSearchTerm("")
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={
              !form.site_name ||
              !form.location_id ||
              !form.customer_ids?.length ||
              !form.address ||
              !form.postcode ||
              isSaving
            }
            variant="confirm"
          >
            {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {editing ? "Save Changes" : "Create Location"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default LocationDialog
